import { Vue, VueConstructor } from 'vue/types/vue'
import { StringMap } from '../utils/map'
import ConfirmationDialog from '../components/confirmation-dialog.vue'
import InfoDialog from '@/shared/components/info-dialog.vue'
import { i18n } from '@/config/i18n'
import { DialogContainer } from '@/shared/dialog/dialog-container'
import { DialogRef } from '@/shared/dialog/dialog-ref'
import { DialogResult } from '@/shared/dialog/dialog-result'

export class DialogService {
  private container?: DialogContainer

  constructor(private app: Vue) {}

  public confirm(text: string, title?: string): Promise<boolean> {
    return new Promise(resolve => {
      const handle = (value: boolean) => () => {
        this.app.$modal.hide(ConfirmationDialog.dialogName)

        resolve(value)
      }

      this.open(ConfirmationDialog.dialogName, {
        cancel: handle(false),
        ok: handle(true),
        text,
        title,
      })
    })
  }

  public hide(name: string | VueConstructor) {
    const dialogName = this.getDialogName(name)

    this.app.$modal.hide(dialogName)
  }

  public info(title: string, text?: string) {
    this.open(InfoDialog, {
      title,
      text,
    })
  }

  public open<Properties extends StringMap<any>>(
    component: string | VueConstructor,
    props?: Properties,
    params?: any,
    events?: any
  ) {
    const dialogName = this.getDialogName(component)

    this.app.$modal.show(dialogName, props, params, events)
  }

  public openDynamic<Input, Output>(
    component: VueConstructor,
    properties?: Input
  ): DialogResult<OutputWithDialogRef<Output>, any> {
    let result: DialogResult<OutputWithDialogRef<Output>, any>

    if (this.container === undefined) {
      throw new Error('No dialog container available')
    } else {
      result = this.container.open(component, properties)
    }

    return result
  }

  public openNyi() {
    this.open(InfoDialog, {
      text: i18n.t('common.dialog.nyi'),
      title: i18n.t('common.dialog.title.nyi'),
    })
  }

  public register(container: DialogContainer) {
    this.container = container
  }

  private getDialogName(name: string | VueConstructor) {
    let dialogName: string

    if (typeof name === 'string') {
      dialogName = name
    } else {
      dialogName = name.dialogName
    }
    return dialogName
  }
}

type OutputWithDialogRef<T> = T & {
  dialogRef: DialogRef<any>
}
